import { useState } from 'react';
import styled from 'styled-components';
import { Box, Divider, Heading, Paragraph, Stack } from '@99designs/emu';
import { useContestTiersConfig } from '../../util/useContestTiersConfig';
import { BaseLayout } from '../BaseLayout';
import { CollaborationMethod, collaborationMethods } from '../CollaborationMethod';
import { Hero } from '../Hero';
import { HowItWorks, howItWorksContent } from '../HowItWorks';
import { PackageInclusions } from '../PackageInclusions';
import { ProductGroupPreview } from '../ProductGroupPreview';
import { ProductGroupSummary } from '../ProductGroupSummary';
import { STICKY_BAR_HEIGHT_PX, StickyBar } from '../StickyBar';
import { DEFAULT_SCROLL_OFFSET_PX, TabId, TabsMenu, TabsMenuDivider, navTabs } from '../TabsMenu';
import type { ZendeskLanguage } from '@99designs/routing';
import type {
    ComparativeProduct,
    ContestProduct,
    ProductGroup,
    QuestionAndAnswer,
    RelatedProduct,
} from '../../types';
import type { InspirationProps } from '../BaseLayout/BaseLayout';

export type Props = {
    productGroup: ProductGroup;
    products: ContestProduct[];
    inspiration?: InspirationProps;
    relatedProducts?: RelatedProduct[];
    faqs: QuestionAndAnswer[];
    lang: ZendeskLanguage;
};

const RightColumnContainer = styled(Box)<{ hasStickyBar: boolean }>`
    padding-top: ${({ hasStickyBar }) => (hasStickyBar ? STICKY_BAR_HEIGHT_PX : 0)}px;
`;

export function ContestLayout({
    productGroup,
    products,
    inspiration,
    relatedProducts,
    faqs,
    lang,
}: Props) {
    const productsSortedByPrice = sortProductsByPrice(products);
    const comparativeProducts: ComparativeProduct[] = useContestTiersConfig(products);
    const [hasStickyBar, setHasStickyBar] = useState<boolean>(false);
    const tabs = navTabs();

    return (
        <BaseLayout
            stickyColumnPosition="right"
            moneyBackGuarantee
            inspiration={inspiration}
            relatedProducts={relatedProducts}
            comparativeProducts={comparativeProducts}
            faqs={
                faqs.length > 0
                    ? { title: productGroup.attributes.title || '', body: faqs }
                    : undefined
            }
            leftColumn={
                <>
                    <Hero images={productGroup.attributes.hero} />

                    <Box
                        display={['none', 'none', 'block', 'block']}
                        marginTop={['none', 'none', '4bu', '4bu']}
                        marginBottom={['none', '3bu', '3bu', '3bu']}
                    >
                        <StickyBar onStickyTriggered={setHasStickyBar}>
                            {/* We assume that sticky bar is always shown when tabs are used */}
                            <TabsMenu
                                tabs={tabs}
                                scrollOffsetTop={DEFAULT_SCROLL_OFFSET_PX + STICKY_BAR_HEIGHT_PX}
                            />
                            <TabsMenuDivider />
                        </StickyBar>
                    </Box>

                    <Box
                        display={['none', 'none', 'block', 'block']}
                        marginTop={['none', 'none', '2bu', '3bu']}
                        marginBottom={['none', 'none', '4bu', '4bu']}
                    >
                        <Stack space="0.75bu">
                            <Heading appearanceLevel={4} accessibilityLevel={2} as="p">
                                {productGroup.attributes.subtitle}
                            </Heading>
                            <Paragraph>{productGroup.attributes.description}</Paragraph>
                        </Stack>
                    </Box>

                    <Box
                        display={['block', 'block', 'none', 'none']}
                        marginTop={['2bu', '3bu', 'none', 'none']}
                    >
                        {productsSortedByPrice.length > 0 && (
                            <ProductGroupSummary
                                title={productGroup.attributes.title}
                                price={productsSortedByPrice[0].pricing}
                                subtitle={productGroup.attributes.subtitle}
                                description={productGroup.attributes.description}
                            />
                        )}
                    </Box>

                    <Box display={['none', 'none', 'block', 'block']}>
                        <Divider />
                    </Box>

                    <Box marginTop={['2bu', '3bu', '3bu', '3bu']}>
                        <Box
                            display={['block', 'block', 'none', 'none']}
                            marginTop={['3bu', '3bu', 'none', 'none']}
                            marginBottom={['3bu', '3bu', 'none', 'none']}
                        >
                            <TabsMenu tabs={tabs} />
                            <TabsMenuDivider />
                        </Box>
                        <Stack space={['none', '4bu', '4bu', '4bu']}>
                            <PackageInclusions
                                inclusions={productGroup.attributes.inclusions || []}
                            />

                            <Box display={['none', 'block', 'block', 'block']}>
                                <Divider />
                            </Box>

                            <Box marginTop={['2bu', 'none', 'none', 'none']}>
                                <CollaborationMethod
                                    type={productGroup.attributes.collaborationMethod}
                                    {...collaborationMethods(
                                        productGroup.attributes.collaborationMethod
                                    )}
                                />
                            </Box>

                            <Box
                                display={['none', 'block', 'block', 'block']}
                                id={TabId.HowItWorks}
                            >
                                <HowItWorks
                                    {...howItWorksContent(
                                        productGroup.attributes.collaborationMethod
                                    )}
                                />
                            </Box>
                        </Stack>
                    </Box>
                </>
            }
            rightColumn={
                <RightColumnContainer
                    display={['none', 'none', 'block', 'block']}
                    hasStickyBar={hasStickyBar}
                >
                    <ProductGroupPreview
                        title={productGroup.attributes.title}
                        price={productsSortedByPrice[0].pricing}
                        inclusions={productGroup.attributes.inclusions}
                    />
                </RightColumnContainer>
            }
            lang={lang}
        />
    );
}

function sortProductsByPrice(products: ContestProduct[]) {
    return products.sort(
        (product, prevProduct) => product.pricing.amountInCents - prevProduct.pricing.amountInCents
    );
}
